import { Controller } from "@hotwired/stimulus"
import { drawTrace } from "../modules/trace_draw"

// The article TRANSPORT: a fixed-bottom waveform whose playhead tracks scroll
// (SEEK). Click anywhere on the strip — or focus it and use ← → / Enter / Space —
// to seek to that block. Segments are derived from the LIVE DOM: every
// [data-block] inside data-target, weighted by its real rendered height (markers
// count 30), so the readout is a measurement of the page, not decoration. It
// re-derives after fonts land and on resize.
//
// Reduced motion: the CSS hides the waveform strip; the block readout (state, not
// animation) still updates and seeks jump with scroll-behavior:auto.
//
// Controller element: .transport[data-target="#article-body"] holding a
// .transport-screen > svg and a [data-role="readout"].
const NS = "http://www.w3.org/2000/svg"
const LABELS = {
  prose: "PROSE", marker: "H2", code: "CODE", diffish: "DIFF", term: "TERM",
  play: "PLAY", video: "VIDEO", fig: "FIG", cta: "CTA", receipts: "RECEIPTS",
}

export default class extends Controller {
  connect() {
    this.body = document.querySelector(this.element.dataset.target)
    this.screen = this.element.querySelector(".transport-screen")
    this.svg = this.screen?.querySelector("svg")
    this.readout = this.element.querySelector("[data-role='readout']")
    if (!this.body || !this.svg || !this.readout) return
    this.els = Array.from(this.body.querySelectorAll("[data-block]"))
    if (!this.els.length) return

    this.segs = []
    this.total = 1
    this.ph = null
    this.lastP = 0
    this._ticking = false

    this._onScroll = this._onScroll.bind(this)
    this._onResize = this._onResize.bind(this)
    this._onClick = this._onClick.bind(this)
    this._onKey = this._onKey.bind(this)

    this._build()
    document.fonts?.ready?.then(() => this._build())

    window.addEventListener("scroll", this._onScroll, { passive: true })
    window.addEventListener("resize", this._onResize, { passive: true })
    this.screen.addEventListener("click", this._onClick)
    this.screen.addEventListener("keydown", this._onKey)
    this._onScroll()
  }

  disconnect() {
    window.removeEventListener("scroll", this._onScroll)
    window.removeEventListener("resize", this._onResize)
    this.screen?.removeEventListener("click", this._onClick)
    this.screen?.removeEventListener("keydown", this._onKey)
    clearTimeout(this._rsz)
  }

  get _reducedMotion() {
    return typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }

  // Measure real block heights — honest weights. drawTrace clears the svg, so the
  // playhead group is re-appended fresh each rebuild (no accumulation).
  _build() {
    this.segs = this.els.map((e) => ({
      type: e.dataset.block,
      el: e,
      w: e.dataset.block === "marker" ? 30 : Math.max(48, e.offsetHeight),
    }))
    this.total = this.segs.reduce((a, s) => a + s.w, 0) || 1
    drawTrace(this.svg, this.segs, 1000, 30, 3, 2.2)
    this.ph = document.createElementNS(NS, "g")
    this.ph.innerHTML =
      '<line class="playhead" x1="0" y1="0" x2="0" y2="30"></line>' +
      '<rect class="playcap" x="-3" y="0" width="6" height="4" rx="1"></rect>'
    this.svg.appendChild(this.ph)
    this._setPlayhead(this.lastP)
  }

  _setPlayhead(p) {
    this.lastP = p = Math.min(1, Math.max(0, p))
    this.ph?.setAttribute("transform", `translate(${(p * 1000).toFixed(1)} 0)`)
    let acc = 0
    let idx = 0
    for (let i = 0; i < this.segs.length; i++) {
      acc += this.segs[i].w / this.total
      if (p <= acc) { idx = i; break }
      idx = i
    }
    const seg = this.segs[idx]
    this.readout.textContent =
      `BLOCK ${String(idx + 1).padStart(2, "0")}/${String(this.segs.length).padStart(2, "0")} · ${LABELS[seg.type] || seg.type.toUpperCase()}`
  }

  _blockAt(p) {
    let acc = 0
    for (const s of this.segs) {
      acc += s.w / this.total
      if (p <= acc) return s
    }
    return this.segs[this.segs.length - 1]
  }

  _seekTo(el) {
    el.scrollIntoView({ behavior: this._reducedMotion ? "auto" : "smooth", block: "center" })
  }

  // rAF as a throttle — one transform write per frame, no layout thrash.
  _onScroll() {
    if (this._ticking) return
    this._ticking = true
    requestAnimationFrame(() => {
      this._ticking = false
      const r = this.body.getBoundingClientRect()
      this._setPlayhead((window.innerHeight * 0.45 - r.top) / r.height)
    })
  }

  _onResize() {
    clearTimeout(this._rsz)
    this._rsz = setTimeout(() => this._build(), 160)
  }

  _onClick(e) {
    const r = this.screen.getBoundingClientRect()
    this._seekTo(this._blockAt((e.clientX - r.left) / r.width).el)
  }

  _onKey(e) {
    if (!["ArrowRight", "ArrowLeft", "Enter", " "].includes(e.key)) return
    e.preventDefault()
    const cur = this.segs.indexOf(this._blockAt(this.lastP))
    if (e.key === "Enter" || e.key === " ") return this._seekTo(this.segs[cur].el)
    const next = Math.min(this.segs.length - 1, Math.max(0, cur + (e.key === "ArrowRight" ? 1 : -1)))
    this._seekTo(this.segs[next].el)
  }
}
